import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Calendar as CalendarIcon, ShieldCheck, ChevronRight, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Logo, LogoArtwork } from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { useI18n } from "@/contexts/I18nContext";
import { useMember } from "@/contexts/MemberContext";
import { api } from "@/lib/api";

export default function RegisterScreen() {
  const { t } = useI18n();
  const { setMember } = useMember();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState("");
  const [dob, setDob] = useState("");
  const [phone, setPhone] = useState("");
  const [maritalStatus, setMaritalStatus] = useState("");
  const [anniversary, setAnniversary] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const requiresAnniversary = maritalStatus === "married";

  const canSubmit = useMemo(() => {
    if (!fullName.trim() || !dob || !phone.trim() || !maritalStatus) return false;
    if (requiresAnniversary && !anniversary) return false;
    return true;
  }, [fullName, dob, phone, maritalStatus, anniversary, requiresAnniversary]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error(t.register.requiredAll);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/members/register", {
        full_name: fullName.trim(),
        date_of_birth: dob,
        phone: phone.trim(),
        marital_status: maritalStatus,
        anniversary: requiresAnniversary ? anniversary : null,
        language: (typeof localStorage !== "undefined" && localStorage.getItem("ncl_lang")) || "es",
      });
      setMember(data);
      toast.success(t.register.success);
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(t.common.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="noise min-h-screen bg-background">
      <div
        className="absolute inset-x-0 top-0 h-72 -z-10 opacity-90"
        style={{
          background:
            "radial-gradient(900px circle at 80% 0%, rgba(200,30,58,0.10) 0%, transparent 55%), radial-gradient(1200px circle at 20% -10%, hsl(var(--accent)) 0%, transparent 55%)",
        }}
      />
      <div className="mx-auto max-w-md px-4 pt-safe">
        <div className="flex items-center justify-end py-4">
          <LanguageToggle />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
          className="flex flex-col items-center text-center"
        >
          <LogoArtwork size={180} className="drop-shadow-sm" />
          <p className="mt-1 text-xs uppercase tracking-[0.18em] text-muted-foreground">{t.tagline}</p>
          <h1 className="font-serif-display text-3xl font-semibold tracking-tight mt-3">{t.register.title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t.register.subtitle}</p>
        </motion.div>

        {/* Public "Meet Our Church" entry point — visible & primary so unregistered visitors can browse the team before signing up. */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, delay: 0.05, ease: [0.2, 0.8, 0.2, 1] }}
        >
          <Card className="mt-6 rounded-2xl shadow-sm ring-1 ring-black/5 overflow-hidden" data-testid="register-meet-our-church-card">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Users className="h-5 w-5 text-primary" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-serif-display text-base font-semibold leading-tight">
                    {t.register.meetTitle}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    {t.register.meetBody}
                  </p>
                </div>
              </div>
              <Button
                asChild
                className="mt-4 h-12 w-full text-base"
                data-testid="register-meet-our-church-button"
              >
                <Link to="/team">
                  {t.register.meetCta}
                  <ChevronRight className="ml-1 h-5 w-5" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        <Card className="mt-6 rounded-2xl shadow-sm ring-1 ring-black/5">
          <CardContent className="p-5">
            <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
              <div className="space-y-1.5">
                <Label htmlFor="register-full-name-input" className="text-sm font-medium">{t.register.fullName}</Label>
                <Input
                  id="register-full-name-input"
                  data-testid="register-full-name-input"
                  className="h-12"
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="register-dob-input" className="text-sm font-medium">{t.register.dob}</Label>
                <Input
                  id="register-dob-input"
                  data-testid="register-dob-input"
                  type="date"
                  className="h-12"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="register-phone-input" className="text-sm font-medium">{t.register.phone}</Label>
                <Input
                  id="register-phone-input"
                  data-testid="register-phone-input"
                  type="tel"
                  className="h-12"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{t.register.maritalStatus}</Label>
                <Select value={maritalStatus} onValueChange={setMaritalStatus}>
                  <SelectTrigger className="h-12" data-testid="register-marital-status-select">
                    <SelectValue placeholder={t.register.maritalStatus} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">{t.register.ms.single}</SelectItem>
                    <SelectItem value="married">{t.register.ms.married}</SelectItem>
                    <SelectItem value="divorced">{t.register.ms.divorced}</SelectItem>
                    <SelectItem value="widowed">{t.register.ms.widowed}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {requiresAnniversary && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  transition={{ duration: 0.2 }}
                  className="space-y-1.5"
                >
                  <Label htmlFor="register-anniversary-input" className="text-sm font-medium flex items-center gap-2">
                    <Heart className="h-4 w-4 text-primary" />
                    {t.register.anniversary}
                  </Label>
                  <Input
                    id="register-anniversary-input"
                    data-testid="register-anniversary-input"
                    type="date"
                    className="h-12"
                    value={anniversary}
                    onChange={(e) => setAnniversary(e.target.value)}
                    required={requiresAnniversary}
                  />
                </motion.div>
              )}

              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="mt-0.5 h-3.5 w-3.5 text-primary" />
                <p>{t.register.privacy}</p>
              </div>

              <Button
                type="submit"
                className="h-12 w-full text-base"
                disabled={!canSubmit || submitting}
                data-testid="register-submit-button"
              >
                {submitting ? t.register.submitting : t.register.submit}
                {!submitting && <ChevronRight className="ml-1 h-5 w-5" />}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-4 flex flex-col items-center text-center" data-testid="register-already-block">
          <p className="text-sm text-muted-foreground">{t.register.alreadyRegistered}</p>
          <Link to="/lookup" className="mt-1 text-sm font-semibold text-primary inline-flex items-center gap-1" data-testid="register-lookup-link">
            {t.register.lookupLink} <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="mt-4 text-center text-xs text-muted-foreground">
          <CalendarIcon className="inline-block mr-1 h-3 w-3" />
          {t.tagline}
        </div>
      </div>
    </div>
  );
}
